import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { changePasswordRequest } from "../../lib/api";
import {
  clearSessionUser,
  getDefaultPathForRole,
  mapApiUserToSessionUser,
  readSessionUser,
  writeSessionUser,
} from "../../lib/authSession";

function SmsChoiceCard({
  title,
  description,
  value,
  error,
  onChange,
}: {
  title: string;
  description: string;
  value: boolean | null;
  error?: string;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="rounded-farm border border-farm-border bg-farm-warm px-3 py-3 space-y-2">
      <div>
        <p className="text-xs font-semibold text-farm-brown">{title}</p>
        <p className="text-[10px] text-farm-muted mt-0.5 leading-relaxed">
          {description}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`h-9 rounded-farm border text-xs font-semibold transition-colors ${
            value === true
              ? "bg-farm-green-pale text-farm-green border-farm-green/30"
              : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
          }`}
        >
          İzin veriyorum
        </button>

        <button
          type="button"
          onClick={() => onChange(false)}
          className={`h-9 rounded-farm border text-xs font-semibold transition-colors ${
            value === false
              ? "bg-red-50 text-red-600 border-red-200"
              : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
          }`}
        >
          İzin vermiyorum
        </button>
      </div>

      {error && <p className="text-[10px] text-red-500">{error}</p>}
    </div>
  );
}

export default function SifreDegistirPage() {
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [sessionUser, setSessionUser] = useState(() => readSessionUser());

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordAgain, setNewPasswordAgain] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showNewAgain, setShowNewAgain] = useState(false);

  const [smsOperationalOptIn, setSmsOperationalOptIn] = useState<boolean | null>(null);
  const [smsMarketingOptIn, setSmsMarketingOptIn] = useState<boolean | null>(null);

  const [error, setError] = useState("");
  const [smsErrors, setSmsErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = readSessionUser();
    if (!next) {
      navigate("/giris", { replace: true });
      return;
    }

    setSessionUser(next);

    if (next.rol === "Müşteri") {
      setSmsOperationalOptIn(
        next.smsOperationalOptIn === undefined ? null : next.smsOperationalOptIn
      );
      setSmsMarketingOptIn(
        next.smsMarketingOptIn === undefined ? null : next.smsMarketingOptIn
      );
    }

    setReady(true);
  }, [navigate]);

  const isCustomer = useMemo(
    () => sessionUser?.rol === "Müşteri",
    [sessionUser]
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSmsErrors({});

    if (!sessionUser) return;

    if (
      !currentPassword.trim() ||
      !newPassword.trim() ||
      !newPasswordAgain.trim()
    ) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    if (newPassword.trim().length < 6) {
      setError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }

    if (newPassword !== newPasswordAgain) {
      setError("Yeni şifre tekrar alanı eşleşmiyor.");
      return;
    }

    if (newPassword === currentPassword) {
      setError("Yeni şifre mevcut şifre ile aynı olamaz.");
      return;
    }

    if (isCustomer) {
      const nextSmsErrors: Record<string, string> = {};

      if (smsOperationalOptIn === null) {
        nextSmsErrors.smsOperationalOptIn =
          "Operasyonel SMS tercihi seçilmelidir.";
      }

      if (smsMarketingOptIn === null) {
        nextSmsErrors.smsMarketingOptIn =
          "Reklam / bilgilendirme SMS tercihi seçilmelidir.";
      }

      if (Object.keys(nextSmsErrors).length > 0) {
        setSmsErrors(nextSmsErrors);
        return;
      }
    }

    setSaving(true);

    try {
      const updated = await changePasswordRequest({
        current_password: currentPassword.trim(),
        new_password: newPassword.trim(),
        sms_operational_opt_in: isCustomer ? smsOperationalOptIn : undefined,
        sms_marketing_opt_in: isCustomer ? smsMarketingOptIn : undefined,
      });

      const nextSession = mapApiUserToSessionUser(updated);
      writeSessionUser(nextSession);
      setSessionUser(nextSession);

      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordAgain("");
      setSuccess("Bilgileriniz başarıyla güncellendi.");

      setTimeout(() => {
        navigate(getDefaultPathForRole(updated.rol), { replace: true });
      }, 700);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("Mevcut şifre")) {
          setError("Mevcut şifre hatalı.");
        } else {
          setError(err.message);
        }
      } else {
        setError("Şifre güncellenemedi.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (!ready || !sessionUser) return null;

  return (
    <div className="min-h-screen bg-farm-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl bg-farm-warm border border-farm-border rounded-farm shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-farm-border bg-farm-cream">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-farm-earth-pale text-farm-earth flex items-center justify-center">
              <i className="ri-lock-password-line text-lg" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-farm-brown">
                Şifre Değiştir
              </h1>
              <p className="text-xs text-farm-muted mt-0.5">
                Hesabınız için yeni bir şifre belirleyin.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2.5 text-[10px] text-farm-muted leading-relaxed">
            <span className="font-semibold text-farm-brown">Kullanıcı:</span>{" "}
            {sessionUser.adSoyad} · {sessionUser.rol}
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
              Mevcut Şifre
            </label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full h-10 px-3 pr-10 text-sm border border-farm-border rounded-farm bg-farm-cream text-farm-brown focus:outline-none focus:border-farm-earth"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-farm-muted"
              >
                <i className={`${showCurrent ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full h-10 px-3 pr-10 text-sm border border-farm-border rounded-farm bg-farm-cream text-farm-brown focus:outline-none focus:border-farm-earth"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-farm-muted"
              >
                <i className={`${showNew ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
              Yeni Şifre Tekrar
            </label>
            <div className="relative">
              <input
                type={showNewAgain ? "text" : "password"}
                value={newPasswordAgain}
                onChange={(e) => setNewPasswordAgain(e.target.value)}
                className="w-full h-10 px-3 pr-10 text-sm border border-farm-border rounded-farm bg-farm-cream text-farm-brown focus:outline-none focus:border-farm-earth"
              />
              <button
                type="button"
                onClick={() => setShowNewAgain((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-farm-muted"
              >
                <i className={`${showNewAgain ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
              </button>
            </div>
          </div>

          {isCustomer && (
            <div className="space-y-3">
              <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3">
                <p className="text-xs font-semibold text-farm-brown">
                  SMS Tercihleri
                </p>
                <p className="text-[10px] text-farm-muted mt-0.5 leading-relaxed">
                  Lütfen her iki ileti türü için tercihinizi seçin. Seçim yapmadan
                  hesabınız aktif kullanıma açılmaz.
                </p>
              </div>

              <SmsChoiceCard
                title="Operasyonel SMS"
                description="Evren Çiftliği tarafından hesabınız, kesim süreci, teslimat durumu, ödeme bilgisi ve siparişinize ilişkin operasyonel SMS bilgilendirmeleri."
                value={smsOperationalOptIn}
                error={smsErrors.smsOperationalOptIn}
                onChange={setSmsOperationalOptIn}
              />

              <SmsChoiceCard
                title="Reklam / Bilgilendirme SMS"
                description="Evren Çiftliği tarafından kampanya, duyuru, tanıtım ve genel bilgilendirme içerikli ticari SMS mesajları."
                value={smsMarketingOptIn}
                error={smsErrors.smsMarketingOptIn}
                onChange={setSmsMarketingOptIn}
              />

              <p className="text-[10px] text-farm-muted -mt-1 leading-relaxed">
                Bu tercihleri daha sonra değiştirebilir veya iptal edebilirsiniz.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-farm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-farm border border-farm-green/30 bg-farm-green-pale px-3 py-2 text-xs text-farm-green">
              {success}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                clearSessionUser();
                navigate("/giris", { replace: true });
              }}
              className="flex-1 h-10 rounded-full border border-farm-border bg-farm-warm text-farm-muted text-xs font-semibold hover:border-farm-earth/40"
            >
              Çıkış Yap
            </button>

            <button
              type="submit"
              disabled={saving}
              className="flex-1 h-10 rounded-full bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown disabled:opacity-70"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}